import {
  DeleteOutlined,
  DownOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  RightOutlined,
  SearchOutlined,
  ShoppingCartOutlined,
} from '@ant-design/icons'
import {
  flexRender,
  getCoreRowModel,
  Row as TableRow,
  useReactTable,
} from '@tanstack/react-table'
import { useVirtualizer, type VirtualItem } from '@tanstack/react-virtual'
import { useDebounce } from '@uidotdev/usehooks'
import { Button, Input, Modal, Select, Space, Tooltip } from 'antd'
import React, {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { toast } from 'sonner'

import DeliveryTime from '@/components/Atoms/DeliveryTime/DeliveryTime'
import Loader from '@/components/Atoms/Loader/Loader'
import QuantityToggler from '@/components/Cart/QuantityToggler/QuantityToggler'
import ValidationErrorModal from '@/components/Cart/ValidationErrorModal/ValidationErrorModal'
import { DEFAULT_REGION_BANK_HEADER_VALUE, DELAY } from '@/constants'
import { ScreenSizeContext } from '@/providers/ScreenSizeProvider'
import { useFeedStore } from '@/stores/feed/feedStore'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import {
  EntityFilterType,
  FormattedFeedProductType,
  OrderProductModel,
  SegmentInterface,
  SegmentShortModel,
  ValidationErrorResponse,
} from '@/types'
import api from '@/utils/api/api'
import { getApiErrorData } from '@/utils/api/errors/errorGuards'
import { handleApiError } from '@/utils/api/errors/handleApiError'
import { getOfferValidationError } from '@/utils/api/errors/offerValidationError'
import { isBuyerUser } from '@/utils/auth/userRoles'
import { trimTrailingZeros } from '@/utils/format/trimTrailingZeros'
import { withVat } from '@/utils/price/vat'
import feedProductsFormatter from '@/utils/products/feedProductsFormatter'
import getProductKey from '@/utils/products/getProductKey'
import { getOrderSelectionBlockText } from '@/utils/products/offerAvailability'
import { toStockOfferLines } from '@/utils/products/offerLines'
import { getRowOfferPrice } from '@/utils/products/offerPrice'

import {
  getBasketColumns,
  getSearchColumns,
  type SelectedItem,
} from './OrderProductsSelectionModal.config'
import cn from './OrderProductsSelectionModal.module.scss'

interface Props {
  open: boolean
  onClose: () => void
  orderCode: number
  onSuccess: () => void
  overrideSegment?: SegmentInterface | SegmentShortModel | null
  existingProducts: OrderProductModel[]
}

const MOBILE_PARENT_HEIGHT = 200
const MOBILE_CHILD_HEIGHT = 140
const DESKTOP_ROW_HEIGHT = 60
const GAP = 12
const CHARS_PER_LINE = 40
const LINE_HEIGHT = 20

function getDescriptionExtraHeight(description: string | undefined): number {
  if (!description) return 0
  const lines = Math.ceil(description.length / CHARS_PER_LINE)
  return Math.max(0, lines - 1) * LINE_HEIGHT
}

const PAGE_SIZE = 10

const ProductCard = ({
  data,
  isChild,
  isExpanded,
  onToggle,
  onBasket,
  isInBasket,
  existingProductsMap,
}: {
  data: FormattedFeedProductType
  isChild: boolean
  isExpanded: boolean
  onToggle: (code: number) => void
  onBasket: (rec: FormattedFeedProductType) => void
  isInBasket: boolean
  activeSegment?: SegmentInterface | SegmentShortModel | null
  existingProductsMap?: Map<string, number>
}) => {
  const isVisible = useFeedStore((state) => state.isSegmentPriceVisible)
  const toggleVisibility = useFeedStore(
    (state) => state.setIsSegmentPriceVisible
  )
  const isClient = useUserInfoStore((state) => isBuyerUser(state.tokenRoles))

  const { nomenclatureDto, suppliersStock } = data
  const stockInfo = suppliersStock?.[0]?.stock?.[0]
  const offerPrice = getRowOfferPrice(data)

  const qtyInOrder = existingProductsMap?.get(String(getProductKey(data)))

  const brandName = nomenclatureDto.brandDto?.description || '-'

  const storeName = stockInfo?.storeShortModel?.description || '-'

  const unit = nomenclatureDto.unitOfMeasurementDto?.description || 'шт'

  const optPrice = withVat(offerPrice?.sellingPrice ?? 0)
  const rrcPrice = withVat(offerPrice?.retailPrice ?? 0)

  const showSegmentPrice = isClient || isVisible

  return (
    <div className={`${cn.mobileCard} ${isChild ? cn.childRow : ''}`}>
      <div className={cn.cardTop}>
        {isChild ? (
          <>
            <div className={cn.childPricesBlock}>
              <div className={cn.childPricesRow}>
                <span className={cn.rrcPrice}>
                  {rrcPrice > 0 ? trimTrailingZeros(rrcPrice) : '-'}
                </span>
                <span className={cn.priceSep}>·</span>
                {showSegmentPrice ? (
                  <span className={cn.optPrice}>
                    {optPrice > 0 ? trimTrailingZeros(optPrice) : '-'}
                  </span>
                ) : (
                  <span className={cn.hiddenPrice}>***</span>
                )}
                <span className={cn.priceCurrency}>BYN</span>
              </div>
              {qtyInOrder !== undefined && qtyInOrder > 0 && (
                <div className={cn.alreadyInOrderBadge}>
                  В заказе: <b>{qtyInOrder}</b> шт.
                </div>
              )}
            </div>
            <span className={cn.article}>{nomenclatureDto.article}</span>
          </>
        ) : (
          <>
            <div className={cn.brandAndArticle}>
              {data.children && (
                <Button
                  type="text"
                  size="small"
                  className={cn.expandBtnMobile}
                  icon={isExpanded ? <DownOutlined /> : <RightOutlined />}
                  onClick={() => onToggle(nomenclatureDto.code)}
                  aria-label={
                    isExpanded
                      ? 'Свернуть остальные предложения'
                      : 'Показать остальные предложения'
                  }
                  aria-expanded={isExpanded}
                />
              )}
              <div className={cn.brandInfo}>
                <span className={cn.brandName}>
                  <strong>Бренд:</strong> {brandName}
                </span>
                <br />
                <span className={cn.article}>{nomenclatureDto.article}</span>
              </div>
            </div>
            <div className={cn.pricesBlock}>
              <div className={cn.rrcPrice}>
                {rrcPrice > 0 ? `${trimTrailingZeros(rrcPrice)} BYN` : '-'}
              </div>
              {showSegmentPrice ? (
                <div className={cn.optPrice}>
                  {optPrice > 0 ? `${trimTrailingZeros(optPrice)} BYN` : '-'}
                </div>
              ) : (
                <div className={cn.hiddenPrice}>*** BYN</div>
              )}
            </div>
          </>
        )}
      </div>

      {!isChild && (
        <div className={cn.cardMiddle}>
          <div className={cn.description}>
            <div className={cn.productTitle}>{nomenclatureDto.description}</div>
            {qtyInOrder !== undefined && qtyInOrder > 0 && (
              <div className={cn.alreadyInOrderBadge}>
                В заказе: <b>{qtyInOrder}</b> шт.
              </div>
            )}
          </div>
        </div>
      )}

      <div className={cn.cardBottom}>
        <div className={cn.stockInfo}>
          <div>
            Наличие:{' '}
            <b>
              {stockInfo?.quantity || 0} {unit}
            </b>
          </div>
          <div>
            Склад: <b>{storeName}</b>
          </div>
          <div className={cn.delivery}>
            Срок:{' '}
            <DeliveryTime
              minDays={stockInfo?.effectiveEtaMinDays}
              maxDays={stockInfo?.effectiveEtaMaxDays}
            />
          </div>
        </div>

        <div className={cn.cardActions}>
          {!isClient && (
            <div
              onClick={() => toggleVisibility(!isVisible)}
              className={cn.visibilityToggle}
            >
              {isVisible ? (
                <EyeOutlined className={cn.eyeIconActive} />
              ) : (
                <EyeInvisibleOutlined className={cn.eyeIcon} />
              )}
            </div>
          )}

          {(() => {
            const blockText = getOrderSelectionBlockText(stockInfo, offerPrice)
            if (blockText !== undefined) {
              return (
                <Tooltip title={blockText}>
                  <span>
                    <Button
                      type="default"
                      shape="circle"
                      className={cn.basketBtn}
                      icon={<ShoppingCartOutlined />}
                      disabled
                    />
                  </span>
                </Tooltip>
              )
            }

            return (
              <Button
                type={isInBasket ? 'primary' : 'default'}
                danger={isInBasket}
                shape="circle"
                className={isInBasket ? cn.basketBtnActive : cn.basketBtn}
                icon={
                  isInBasket ? <DeleteOutlined /> : <ShoppingCartOutlined />
                }
                onClick={() => onBasket(data)}
              />
            )
          })()}
        </div>
      </div>
    </div>
  )
}

const MobileBasketCard = ({
  item,
  onBasket,
  updateQuantity,
}: {
  item: SelectedItem
  onBasket: (rec: FormattedFeedProductType) => void
  updateQuantity: (key: string, qty: number | null) => void
  activeSegment?: SegmentInterface | SegmentShortModel | null
}) => {
  const isVisible = useFeedStore((state) => state.isSegmentPriceVisible)
  const isClient = useUserInfoStore((state) => isBuyerUser(state.tokenRoles))

  const { product, quantity, productKey } = item
  const { nomenclatureDto, suppliersStock } = product
  const stockInfo = suppliersStock?.[0]?.stock?.[0]
  const offerPrice = getRowOfferPrice(product)

  const brandName = nomenclatureDto.brandDto?.description || '-'
  const storeName = stockInfo?.storeShortModel?.description || '-'
  const unit = nomenclatureDto.unitOfMeasurementDto?.description || 'шт'

  const optPrice = withVat(offerPrice?.sellingPrice ?? 0)
  const rrcPrice = withVat(offerPrice?.retailPrice ?? 0)

  const showSegmentPrice = isClient || isVisible

  return (
    <div className={cn.mobileCard}>
      <div className={cn.cardTop}>
        <div className={cn.brandAndArticle}>
          <div className={cn.brandInfo}>
            <span className={cn.brandName}>
              <strong>Бренд:</strong> {brandName}
            </span>
            <br />
            <span className={cn.article}>{nomenclatureDto.article}</span>
          </div>
        </div>
        <div className={cn.pricesBlock}>
          <div className={cn.rrcPrice}>{trimTrailingZeros(rrcPrice)} BYN</div>
          {showSegmentPrice ? (
            <div className={cn.optPrice}>{trimTrailingZeros(optPrice)} BYN</div>
          ) : (
            <div className={cn.hiddenPrice}>*** BYN</div>
          )}
        </div>
      </div>

      <div className={cn.cardMiddle}>
        <div className={cn.description}>
          <div className={cn.productTitle}>{nomenclatureDto.description}</div>
        </div>
      </div>

      <div className={cn.cardBottom}>
        <div className={cn.stockInfo}>
          <div>
            Наличие:{' '}
            <b>
              {stockInfo?.quantity || 0} {unit}
            </b>
          </div>
          <div>
            Склад: <b>{storeName}</b>
          </div>
          <div className={cn.delivery}>
            Срок:{' '}
            <DeliveryTime
              minDays={stockInfo?.effectiveEtaMinDays}
              maxDays={stockInfo?.effectiveEtaMaxDays}
            />
          </div>
        </div>

        <div className={`${cn.cardActions} ${cn.cardActionsCompact}`}>
          <QuantityToggler
            location="orderSelection"
            element={product}
            productQuantity={quantity}
            maxQuantity={stockInfo?.quantity || 0}
            onQuantityChange={(val) => updateQuantity(productKey, val)}
          />
          <Button
            type="primary"
            danger
            shape="circle"
            icon={<DeleteOutlined />}
            onClick={() => onBasket(product)}
          />
        </div>
      </div>
    </div>
  )
}

const Row = memo(
  ({
    virtualRow,
    row,
    isLoaderRow,
    isExpanded,
    isMobile,
    onToggle,
    onBasket,
    isInBasket,
    activeSegment,
    existingProductsMap,
  }: {
    virtualRow: VirtualItem
    row?: TableRow<FormattedFeedProductType>
    isLoaderRow: boolean
    basketHash: number
    isExpanded: boolean
    isMobile: boolean
    onToggle: (code: number) => void
    onBasket: (rec: FormattedFeedProductType) => void
    isInBasket: boolean
    activeSegment?: SegmentInterface | SegmentShortModel | null
    existingProductsMap?: Map<string, number>
  }) => {
    const isChild = row?.original?.parent === false

    return (
      <div
        className={`${cn.virtualItem} ${
          isMobile
            ? cn.mobileRowWrapper
            : `${cn.virtualRow} ${isChild ? cn.childRow : ''}`
        }`}
        style={{
          '--virtual-row-size': `${virtualRow.size}px`,
          '--virtual-row-start': `${virtualRow.start}px`,
        }}
      >
        {isLoaderRow ? (
          <div className={cn.loaderRow}>
            <Loader size={20} />
          </div>
        ) : isMobile ? (
          <ProductCard
            data={row!.original}
            isChild={isChild}
            isExpanded={isExpanded}
            onToggle={onToggle}
            onBasket={onBasket}
            isInBasket={isInBasket}
            activeSegment={activeSegment}
            existingProductsMap={existingProductsMap}
          />
        ) : (
          row?.getVisibleCells().map((cell) => (
            <div key={cell.id} className={cn.td}>
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </div>
          ))
        )}
      </div>
    )
  },
  (prev, next) => {
    return (
      prev.virtualRow.start === next.virtualRow.start &&
      prev.row?.id === next.row?.id &&
      prev.basketHash === next.basketHash &&
      prev.isLoaderRow === next.isLoaderRow &&
      prev.isExpanded === next.isExpanded &&
      prev.isMobile === next.isMobile &&
      prev.isInBasket === next.isInBasket
    )
  }
)

const OrderProductsSelectionModal: React.FC<Props> = ({
  open,
  onClose,
  orderCode,
  onSuccess,
  overrideSegment,
  existingProducts,
}) => {
  const { contextSegment: globalSegment } = useUserInfoStore()

  const [searchValue, setSearchValue] = useState('')
  const [selectedBrandId, setSelectedBrandId] = useState<string | null>(null)
  const [items, setItems] = useState<FormattedFeedProductType[]>([])

  const [availableBrands, setAvailableBrands] = useState<EntityFilterType[]>([])

  const [isLoading, setIsLoading] = useState(false)
  const [isFetchingNextPage, setIsFetchingNextPage] = useState(false)
  const [page, setPage] = useState(0)
  const [hasMore, setHasMore] = useState(true)

  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set())
  const [basket, setBasket] = useState<Map<string, SelectedItem>>(new Map())
  const [isBasketVisible, setIsBasketVisible] = useState(false)
  const [confirmLoading, setConfirmLoading] = useState(false)

  const isRequestInFlight = useRef(false)
  const parentRef = useRef<HTMLDivElement>(null)
  const debouncedSearch = useDebounce(searchValue, DELAY)
  const [modal, contextHolder] = Modal.useModal()
  const [validationError, setValidationError] =
    useState<ValidationErrorResponse | null>(null)

  const basketData = useMemo(() => Array.from(basket.values()), [basket])

  const activeSegment = useMemo(() => {
    return overrideSegment !== undefined ? overrideSegment : globalSegment
  }, [overrideSegment, globalSegment])

  const existingProductsMap = useMemo(() => {
    const map = new Map<string, number>()
    existingProducts.forEach((p) => {
      const key = String(getProductKey(p))
      const qty = p.quantity || 0
      const currentQty = map.get(key) || 0
      map.set(key, currentQty + qty)
    })
    return map
  }, [existingProducts])

  const fetchItems = useCallback(
    async (pageNum: number, isNewSearch: boolean) => {
      if (isRequestInFlight.current && !isNewSearch) return
      isRequestInFlight.current = true

      if (isNewSearch) {
        setIsLoading(true)
        setPage(0)
        setItems([])
        setExpandedRows(new Set())
        setHasMore(true)
      } else {
        setIsFetchingNextPage(true)
      }

      try {
        const response = await api.get('/nomenclatures/search', {
          params: {
            query: debouncedSearch.trim(),
            orderCode,
            brandIds: selectedBrandId || undefined,
            withLeftovers: false,
            page: pageNum,
            pageSize: PAGE_SIZE,
          },
          headers: { 'Region-Bank': DEFAULT_REGION_BANK_HEADER_VALUE },
        })
        const result = response.data.result || response.data
        const formatted = feedProductsFormatter(
          result.pagedProducts.content || []
        )

        if (result.availableFilters?.brands) {
          if (pageNum === 0) {
            setAvailableBrands(result.availableFilters.brands)
          }
        }

        setItems((prev) => (isNewSearch ? formatted : [...prev, ...formatted]))
        setHasMore(formatted.length === PAGE_SIZE)
        if (!isNewSearch) setPage(pageNum)
      } catch (error: unknown) {
        const errorData = getApiErrorData(error)
        const msg = errorData?.message || 'Ошибка загрузки'
        const tid = errorData?.traceId
        toast.error(msg, { description: tid ? `Trace ID: ${tid}` : undefined })
      } finally {
        setIsLoading(false)
        setIsFetchingNextPage(false)
        isRequestInFlight.current = false
      }
    },
    [debouncedSearch, selectedBrandId, orderCode]
  )

  useEffect(() => {
    if (open) fetchItems(0, true)
  }, [debouncedSearch, selectedBrandId, open, fetchItems])

  const handleScroll = useCallback(() => {
    const container = parentRef.current
    if (!container) return
    const { scrollTop, scrollHeight, clientHeight } = container
    if (
      scrollHeight - scrollTop <= clientHeight + 100 &&
      hasMore &&
      !isFetchingNextPage &&
      !isLoading
    ) {
      fetchItems(page + 1, false)
    }
  }, [hasMore, isFetchingNextPage, isLoading, fetchItems, page])

  const toggleInBasket = useCallback((rec: FormattedFeedProductType) => {
    const blockText = getOrderSelectionBlockText(
      rec.suppliersStock[0]?.stock[0],
      getRowOfferPrice(rec)
    )

    if (blockText !== undefined) {
      toast.error(blockText)
      return
    }

    const key = String(getProductKey(rec))
    setBasket((prev) => {
      const next = new Map(prev)
      if (next.has(key)) {
        next.delete(key)
        toast.info(`Удалено: ${rec.nomenclatureDto.description}`)
      } else {
        next.set(key, { product: rec, quantity: 1, productKey: key })
        toast.success(`Добавлено: ${rec.nomenclatureDto.description}`)
      }
      return next
    })
  }, [])

  const updateQuantity = useCallback((key: string, qty: number | null) => {
    if (!qty || qty < 1) return
    setBasket((prev) => {
      const next = new Map(prev)
      const entry = next.get(key)
      if (entry) {
        const max = entry.product.suppliersStock?.[0]?.stock?.[0]?.quantity || 0
        if (qty > max) {
          toast.warning(`Доступно только ${max} шт.`)
          next.set(key, { ...entry, quantity: max })
        } else {
          next.set(key, { ...entry, quantity: qty })
        }
      }
      return next
    })
  }, [])

  const toggleRow = useCallback((code: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev)
      if (next.has(code)) next.delete(code)
      else next.add(code)
      return next
    })
  }, [])

  const flatData = useMemo(() => {
    const res: FormattedFeedProductType[] = []
    items.forEach((item) => {
      res.push(item)
      if (expandedRows.has(item.nomenclatureDto.code) && item.children)
        res.push(...item.children)
    })
    return res
  }, [items, expandedRows])

  const searchColumns = useMemo(
    () =>
      getSearchColumns({
        basket,
        toggleInBasket,
        contextSegment: activeSegment,
        expandedRows,
        toggleRow,
        existingProductsMap,
      }),
    [
      basket,
      expandedRows,
      activeSegment,
      toggleInBasket,
      toggleRow,
      existingProductsMap,
    ]
  )

  const basketColumns = useMemo(
    () =>
      getBasketColumns({
        updateQuantity,
        toggleInBasket,
        contextSegment: activeSegment,
      }),
    [updateQuantity, toggleInBasket, activeSegment]
  )

  const tableSearch = useReactTable({
    data: flatData,
    columns: searchColumns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => String(getProductKey(row)),
  })

  const tableBasket = useReactTable({
    data: basketData,
    columns: basketColumns,
    getCoreRowModel: getCoreRowModel(),
  })

  const { rows } = tableSearch.getRowModel()

  const screen = useContext(ScreenSizeContext)
  const isMobile = [screen?.sm, screen?.xs].some((v) => v)

  const rowVirtualizer = useVirtualizer({
    count: hasMore ? rows.length + 1 : rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: useCallback(
      (index: number) => {
        if (!isMobile) return DESKTOP_ROW_HEIGHT
        const row = rows[index]
        if (!row) return 80
        const isChild = row.original?.parent === false
        const extra = isChild
          ? 0
          : getDescriptionExtraHeight(
              row.original?.nomenclatureDto?.description
            )
        return (
          (isChild ? MOBILE_CHILD_HEIGHT : MOBILE_PARENT_HEIGHT) + extra + GAP
        )
      },
      [isMobile, rows]
    ),
    overscan: 5,
  })

  const totals = useMemo(() => {
    let opt = 0,
      rrc = 0
    basket.forEach((item) => {
      const price = getRowOfferPrice(item.product)
      opt += withVat((price?.sellingPrice ?? 0) * item.quantity)
      rrc += withVat((price?.retailPrice ?? 0) * item.quantity)
    })
    return { opt, rrc, count: basket.size }
  }, [basket])

  const handleConfirm = async () => {
    setConfirmLoading(true)
    const payload = {
      products: basketData.map((i) => ({
        key: {
          supplierId:
            i.product.suppliersStock[0].stock[0].storeShortModel.supplierId,
          productCode: i.product.nomenclatureDto.code,
          storeCode: i.product.suppliersStock[0].stock[0].storeShortModel.code,
        },
        quantity: i.quantity,
        route: i.product.suppliersStock?.[0]?.stock?.[0]?.route ?? [],
      })),
    }
    try {
      await api.post(`/orders/${orderCode}/products`, payload, {
        headers: { 'Region-Bank': DEFAULT_REGION_BANK_HEADER_VALUE },
      })
      toast.success('Товары добавлены в заказ')
      setBasket(new Map())
      onSuccess()
      onClose()
    } catch (e) {
      const offerError = getOfferValidationError(e)
      if (offerError) {
        setValidationError(offerError)
      } else {
        handleApiError(e, modal)
      }
    } finally {
      setConfirmLoading(false)
    }
  }

  return (
    <Modal
      title={`Подбор товаров в заказ №${orderCode}`}
      open={open}
      onCancel={onClose}
      width={1300}
      centered
      footer={
        <div className={cn.modalFooter}>
          <div className={cn.totals}>
            <span>
              Итого ОПТ: <strong>{trimTrailingZeros(totals.opt)}</strong>
            </span>
            <span>
              Итого РРЦ: <strong>{trimTrailingZeros(totals.rrc)}</strong>
            </span>
          </div>
          <Space>
            <Button onClick={onClose}>Закрыть</Button>
            <Button
              type="primary"
              loading={confirmLoading}
              disabled={basket.size === 0}
              onClick={handleConfirm}
              className={cn.confirmBtn}
            >
              Добавить в заказ {basket.size > 0 && `(${basket.size})`}
            </Button>
          </Space>
        </div>
      }
    >
      <div className={cn.filters}>
        <Input
          placeholder="Наименование или артикул..."
          prefix={<SearchOutlined />}
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          className={cn.searchInp}
          allowClear
        />
        <Select
          placeholder="Бренд"
          className={cn.brandSel}
          options={availableBrands.map((b) => ({
            label: `${b.description}`,
            value: b.id,
          }))}
          onChange={setSelectedBrandId}
          allowClear
          showSearch
          optionFilterProp="label"
        />
      </div>

      <div
        className={`${cn.tableWrapper} ${isBasketVisible ? cn.tableWrapperCompact : ''}`}
      >
        {!isMobile && (
          <div className={cn.tableHeader}>
            {tableSearch.getHeaderGroups().map((hg) => (
              <div key={hg.id} className={cn.tr}>
                {hg.headers.map((h) => (
                  <div key={h.id} className={cn.th}>
                    {flexRender(h.column.columnDef.header, h.getContext())}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
        <div ref={parentRef} className={cn.tableBody} onScroll={handleScroll}>
          {isLoading ? (
            <div className={cn.initialLoader}>
              <Loader size={40} />
            </div>
          ) : items.length === 0 ? (
            <div className={cn.emptyMessage}>Товары не найдены</div>
          ) : (
            <div
              className={cn.virtualList}
              style={{
                '--virtual-total-size': `${rowVirtualizer.getTotalSize()}px`,
              }}
            >
              {rowVirtualizer.getVirtualItems().map((vr) => {
                const isLoaderRow = vr.index > rows.length - 1
                const row = rows[vr.index]
                const itemCode = row?.original?.nomenclatureDto?.code
                const isExpanded = itemCode ? expandedRows.has(itemCode) : false
                const isInBasket = row
                  ? basket.has(String(getProductKey(row.original)))
                  : false

                return (
                  <Row
                    key={vr.key}
                    virtualRow={vr}
                    row={row}
                    isLoaderRow={isLoaderRow}
                    basketHash={basket.size}
                    isExpanded={isExpanded}
                    isMobile={isMobile}
                    onToggle={toggleRow}
                    onBasket={toggleInBasket}
                    isInBasket={isInBasket}
                    activeSegment={activeSegment}
                    existingProductsMap={existingProductsMap}
                  />
                )
              })}
            </div>
          )}
        </div>
      </div>

      <div className={cn.basketDivider}>
        <Button
          type="link"
          onClick={() => setIsBasketVisible(!isBasketVisible)}
          className={cn.basketToggleBtn}
        >
          {isBasketVisible
            ? 'Скрыть товары'
            : `Показать товары (${basket.size})`}
        </Button>
      </div>

      {isBasketVisible && (
        <div className={cn.basketWrapper}>
          {!isMobile && (
            <div className={cn.basketHeader}>
              {tableBasket.getHeaderGroups().map((hg) => (
                <div key={hg.id} className={cn.basketTr}>
                  {hg.headers.map((h) => (
                    <div key={h.id} className={cn.th}>
                      {flexRender(h.column.columnDef.header, h.getContext())}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}

          <div className={cn.basketBody}>
            {isMobile
              ? basketData.map((item) => (
                  <MobileBasketCard
                    key={item.productKey}
                    item={item}
                    onBasket={toggleInBasket}
                    updateQuantity={updateQuantity}
                    activeSegment={activeSegment}
                  />
                ))
              : tableBasket.getRowModel().rows.map((row) => (
                  <div key={row.id} className={cn.basketTr}>
                    {row.getVisibleCells().map((cell) => (
                      <div key={cell.id} className={cn.td}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </div>
                    ))}
                  </div>
                ))}
            {basketData.length === 0 && (
              <div className={cn.emptyBasket}>Товары отсутствуют</div>
            )}
          </div>
        </div>
      )}
      {contextHolder}
      <ValidationErrorModal
        error={validationError}
        lines={toStockOfferLines(basketData.map((item) => item.product))}
        onClose={() => setValidationError(null)}
      />
    </Modal>
  )
}

export default OrderProductsSelectionModal
